import {
  FunctionComponent,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { toCurrency } from 'utilities/number.util'
import lineChartCss from './lineChart.module.scss'

export type LineChartPoint = {
  label: string
  value: number
}

type LineChartProps = {
  points: LineChartPoint[]
  color: string
  /** Maximum height; narrow screens get a shorter chart to keep proportions. */
  height?: number
  ariaLabel?: string
}

// The right side mirrors the gap between the y-axis labels and the left edge.
const PAD = { top: 28, right: 12, bottom: 28, left: 52 }
const INK_MUTED = 'rgba(58,42,36,0.45)'

const formatAxis = (value: number) =>
  value >= 1000
    ? `${(value / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`
    : value.toLocaleString('pt-BR', { maximumFractionDigits: 0 })

/** Rounds the max value up to a "nice" scale with 4-6 ticks. */
const niceScale = (value: number) => {
  const raw = (value * 1.08) / 5
  const magnitude = 10 ** Math.floor(Math.log10(raw))
  const step =
    [1, 2, 2.5, 5, 10].map((n) => n * magnitude).find((n) => n >= raw) ??
    10 * magnitude
  const ticks = Math.max(1, Math.ceil((value * 1.08) / step))

  return { max: step * ticks, ticks }
}

const formatValue = (value: number, compact: boolean) =>
  compact
    ? `${(value / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`
    : value.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })

const useElementWidth = () => {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)

  useLayoutEffect(() => {
    if (ref.current) {
      setWidth(ref.current.clientWidth)
    }
  }, [])

  useEffect(() => {
    const element = ref.current

    if (!element || !('ResizeObserver' in window)) {
      return
    }

    const observer = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width)
    )
    observer.observe(element)

    return () => observer.disconnect()
  }, [])

  return [ref, width] as const
}

/**
 * Line chart with an area fill, value labels on every point and a
 * hover tooltip. Labels are abbreviated/thinned out on narrow screens, and
 * the month axis falls back to just the first and last month.
 */
const LineChart: FunctionComponent<LineChartProps> = ({
  points,
  color,
  height: maxHeight = 300,
  ariaLabel,
}) => {
  const gradientId = useId()
  const [containerRef, containerWidth] = useElementWidth()
  const [hovered, setHovered] = useState<number | null>(null)

  const width = Math.max(containerWidth, 320)
  const height = Math.round(Math.min(maxHeight, Math.max(200, width * 0.6)))
  const innerW = width - PAD.left - PAD.right
  const innerH = height - PAD.top - PAD.bottom
  const { max, ticks } = niceScale(Math.max(...points.map((p) => p.value), 1))

  const x = (i: number) =>
    PAD.left + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW)
  const y = (value: number) => PAD.top + innerH - (value / max) * innerH

  const coords = points.map((point, i) => [x(i), y(point.value)] as const)
  const line = coords
    .map(([cx, cy], i) => `${i === 0 ? 'M' : 'L'} ${cx.toFixed(1)} ${cy.toFixed(1)}`)
    .join(' ')
  const baseline = PAD.top + innerH
  const area = `${line} L ${x(points.length - 1).toFixed(1)} ${baseline} L ${x(0).toFixed(1)} ${baseline} Z`

  const step = points.length > 1 ? innerW / (points.length - 1) : innerW
  const compact = step < 84
  const every = step < 48 ? 2 : 1
  const isLabelShown = (i: number) => (points.length - 1 - i) % every === 0
  // Month labels (~50px wide) collide below this spacing: keep only the ends.
  const axisEdgesOnly = step < 64
  const isAxisLabelShown = (i: number) =>
    axisEdgesOnly ? i === 0 || i === points.length - 1 : isLabelShown(i)
  const anchor = (i: number) =>
    i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle'
  const edgeOffset = (i: number) =>
    i === 0 ? -6 : i === points.length - 1 ? 6 : 0

  const hoveredPoint = hovered !== null ? points[hovered] : null
  const scale = containerWidth > 0 ? containerWidth / width : 1

  return (
    <div ref={containerRef} className={lineChartCss.container}>
      {containerWidth > 0 && (
        <svg
          className={lineChartCss.svg}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label={ariaLabel}
          onMouseLeave={() => setHovered(null)}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.16" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>

          {Array.from({ length: ticks + 1 }, (_, i) => {
            const value = (max / ticks) * (ticks - i)
            const tickY = y(value)
            const isBaseline = i === ticks

            return (
              <g key={i}>
                <line
                  x1={PAD.left}
                  x2={width - PAD.right}
                  y1={tickY}
                  y2={tickY}
                  stroke={`rgba(58,42,36,${isBaseline ? 0.14 : 0.06})`}
                  strokeDasharray={isBaseline ? undefined : '3 4'}
                />
                <text
                  x={PAD.left - 12}
                  y={tickY + 4}
                  textAnchor="end"
                  fill={INK_MUTED}
                  fontSize={11}
                >
                  {formatAxis(value)}
                </text>
              </g>
            )
          })}

          {points.map((point, i) =>
            isAxisLabelShown(i) ? (
              <text
                key={point.label}
                x={x(i) + edgeOffset(i)}
                y={height - 6}
                textAnchor={anchor(i)}
                fill={INK_MUTED}
                fontSize={11}
              >
                {point.label}
              </text>
            ) : null
          )}

          <path d={area} fill={`url(#${gradientId})`} />
          <path
            d={line}
            fill="none"
            stroke={color}
            strokeWidth={1.5}
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {coords.map(([cx, cy], i) => (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={2.75}
              fill="#fff"
              stroke={color}
              strokeWidth={1.25}
            />
          ))}

          {coords.map(([cx, cy], i) =>
            isLabelShown(i) ? (
              <text
                key={i}
                x={cx + edgeOffset(i)}
                y={cy - 10}
                textAnchor={anchor(i)}
                fill={color}
                fontSize={11}
                fontWeight={500}
                stroke="#fff"
                strokeWidth={4}
                strokeLinejoin="round"
                paintOrder="stroke"
              >
                {formatValue(points[i].value, compact)}
              </text>
            ) : null
          )}

          {hovered !== null && (
            <>
              <line
                x1={coords[hovered][0]}
                x2={coords[hovered][0]}
                y1={PAD.top}
                y2={baseline}
                stroke="rgba(58,42,36,0.18)"
              />
              <circle
                cx={coords[hovered][0]}
                cy={coords[hovered][1]}
                r={5}
                fill={color}
                stroke="#fff"
                strokeWidth={2}
              />
            </>
          )}

          {coords.map(([cx], i) => (
            <rect
              key={i}
              x={cx - step / 2}
              y={PAD.top}
              width={step}
              height={innerH}
              fill="transparent"
              onMouseEnter={() => setHovered(i)}
            />
          ))}
        </svg>
      )}

      {hoveredPoint && hovered !== null && (
        <div
          className={lineChartCss.tooltip}
          role="tooltip"
          style={{
            left: coords[hovered][0] * scale,
            top: coords[hovered][1] * scale,
          }}
        >
          <span className={lineChartCss.tooltipLabel}>{hoveredPoint.label}</span>
          <span className={lineChartCss.tooltipValue}>
            {toCurrency(hoveredPoint.value)}
          </span>
        </div>
      )}
    </div>
  )
}

export default LineChart
